<template>
    <el-dialog
        :visible.sync="visible"
        :width="dialogWidth"
        center
        append-to-body
        custom-class="subscription-follow-dialog"
        @close="handleClose"
    >
        <div slot="title" class="subscription-follow-title">
            <i class="el-icon-star-off"></i>
            <span>{{ title }}</span>
        </div>
        <div class="subscription-follow-body">
            <p class="subscription-follow-desc">{{ desc }}</p>
            <div class="subscription-follow-qr-wrap" v-if="qrImage">
                <img class="subscription-follow-qr" :src="qrImage" :alt="accountName">
            </div>
            <div class="subscription-follow-account">{{ accountName }}</div>
            <p class="subscription-follow-hint">{{ hint }}</p>
            <div class="subscription-follow-footer">
                <el-button @click="handleLater">稍后再说</el-button>
                <el-button type="primary" @click="handleDone">我知道了</el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import {
    SUBSCRIPTION_FOLLOW_ACCOUNT_NAME,
    SUBSCRIPTION_FOLLOW_DESC,
    SUBSCRIPTION_FOLLOW_ENABLED,
    SUBSCRIPTION_FOLLOW_HINT,
    SUBSCRIPTION_FOLLOW_QR_IMAGE,
    SUBSCRIPTION_FOLLOW_REMIND_DAYS,
    SUBSCRIPTION_FOLLOW_TITLE
} from '@/config'

const STORAGE_KEY = 'dbtool_subscription_follow_prompt_v1'

function safeParseStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (e) {
        return {}
    }
}

export default {
    name: 'SubscriptionFollowDialog',
    data() {
        return {
            visible: false,
            dialogWidth: '92vw',
            promptTimer: null,
            activeUserKey: '',
            closeRememberDays: null
        }
    },
    computed: {
        enabled() {
            return !!SUBSCRIPTION_FOLLOW_ENABLED
        },
        title() {
            return SUBSCRIPTION_FOLLOW_TITLE
        },
        desc() {
            return SUBSCRIPTION_FOLLOW_DESC
        },
        accountName() {
            return SUBSCRIPTION_FOLLOW_ACCOUNT_NAME
        },
        hint() {
            return SUBSCRIPTION_FOLLOW_HINT
        },
        qrImage() {
            return SUBSCRIPTION_FOLLOW_QR_IMAGE
        }
    },
    watch: {
        '$store.state.user'(user, oldUser) {
            this.handleUserChange(user, oldUser)
        }
    },
    created() {
        if (typeof window !== 'undefined' && window.innerWidth > 768) {
            this.dialogWidth = '430px'
        }
    },
    mounted() {
        this._resizeHandler = () => {
            this.dialogWidth = window.innerWidth > 768 ? '430px' : '92vw'
        }
        window.addEventListener('resize', this._resizeHandler)
    },
    beforeDestroy() {
        if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler)
        if (this.promptTimer) clearTimeout(this.promptTimer)
    },
    methods: {
        getUserKey(user) {
            if (!user) return ''
            return String(user.id || user.username || user.token || '').trim()
        },
        handleUserChange(user, oldUser) {
            const newToken = user && user.token
            const oldToken = oldUser && oldUser.token
            if (!newToken) {
                this.visible = false
                this.activeUserKey = ''
                if (this.promptTimer) clearTimeout(this.promptTimer)
                return
            }
            if (newToken === oldToken) return
            this.schedulePrompt(user)
        },
        shouldPrompt(user) {
            if (!this.enabled || !this.qrImage) return false
            const userKey = this.getUserKey(user)
            if (!userKey) return false
            const records = safeParseStorage()
            const record = records[userKey]
            return !(record && record.until && Number(record.until) > Date.now())
        },
        schedulePrompt(user) {
            if (!this.shouldPrompt(user)) return
            this.activeUserKey = this.getUserKey(user)
            if (this.promptTimer) clearTimeout(this.promptTimer)
            this.promptTimer = setTimeout(() => {
                const currentUser = this.$store.state.user
                if (!currentUser || this.getUserKey(currentUser) !== this.activeUserKey) return
                if (this.$store.state.showLoginDialog) {
                    this.schedulePrompt(currentUser)
                    return
                }
                if (!this.shouldPrompt(currentUser)) return
                this.visible = true
            }, 500)
        },
        rememberPrompt(days) {
            const user = this.$store.state.user
            const userKey = this.getUserKey(user)
            if (!userKey) return
            const records = safeParseStorage()
            records[userKey] = {
                until: Date.now() + Math.max(1, days) * 24 * 60 * 60 * 1000
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(records))
        },
        handleLater() {
            this.closeRememberDays = 1
            this.visible = false
        },
        handleDone() {
            this.closeRememberDays = SUBSCRIPTION_FOLLOW_REMIND_DAYS
            this.visible = false
        },
        handleClose() {
            const days = this.closeRememberDays == null ? 1 : this.closeRememberDays
            this.closeRememberDays = null
            this.rememberPrompt(days)
        }
    }
}
</script>

<style lang="less" scoped>
.subscription-follow-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
}

.subscription-follow-title i {
    font-size: 20px;
    color: #67c23a;
}

.subscription-follow-body {
    padding-top: 4px;
}

.subscription-follow-desc {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.7;
    color: #606266;
}

.subscription-follow-qr-wrap {
    width: 100%;
    max-width: 280px;
    margin: 0 auto 14px;
    padding: 14px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 18px;
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.subscription-follow-qr {
    display: block;
    width: 100%;
    border-radius: 10px;
}

.subscription-follow-account {
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
}

.subscription-follow-hint {
    margin: 0 0 20px;
    text-align: center;
    font-size: 13px;
    line-height: 1.6;
    color: #909399;
}

.subscription-follow-footer {
    display: flex;
    justify-content: center;
    gap: 12px;
}

.dark-mode .subscription-follow-title {
    color: var(--text-primary);
}

.dark-mode .subscription-follow-desc {
    color: var(--text-regular);
}

.dark-mode .subscription-follow-qr-wrap {
    background: #fff;
    border-color: rgba(255, 255, 255, 0.12);
}

.dark-mode .subscription-follow-account {
    color: var(--text-primary);
}

.dark-mode .subscription-follow-hint {
    color: var(--text-secondary);
}
</style>
